import React, { useState, useEffect, useMemo } from 'react';
import {
  PlugIcon,
  ZapIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  TrendingUpIcon,
  CalendarIcon,
  BoltIcon
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { thingsboardService } from '../../services/thingsboard';
import { whToKwh } from '../../utils/energyUtils';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';

interface OutletData {
  id: string;
  name: string;
  location: string;
  usage: number;
  percent: number;
  todayUsage: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const { t } = useTranslation();
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0f172a] border border-slate-700/80 rounded-lg px-2.5 py-1.5 shadow-2xl z-50">
        <p className="text-[0.65rem] text-slate-300 font-mono">
          {t('monthly_energy.day_label', { label })}
        </p>
        <p className="text-xs text-cyan-300 font-mono font-bold">
          {payload[0].value.toFixed(3)}{' '}
          <span className="text-slate-400 text-[0.6rem]">kWh</span>
        </p>
      </div>
    );
  }
  return null;
};

export function TopOutletWidget({ seoSensors = [], sensorTelemetry = {} }: { seoSensors?: any[]; sensorTelemetry?: Record<string, any> }) {
  const { t } = useTranslation();
  const [selectedOutlet, setSelectedOutlet] = useState<any | null>(null);
  const [deviceCategory, setDeviceCategory] = useState<'multitab' | 'outlet'>('multitab');
  const [timeRange, setTimeRange] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [latestStats, setLatestStats] = useState<Record<string, { daily: number; weekly: number; monthly: number }>>({});
  const [loading, setLoading] = useState(false);

  const today = new Date();
  const currentDay = today.getDate();
  const monthLabel = `${today.getFullYear()} ${t('common.year')} ${today.getMonth() + 1}${t('common.month')}`;

  // Fetch telemetry history for selected outlet
  const { data: historyData, loading: historyLoading } = useTelemetryHistory(
    selectedOutlet?.id || '',
    ['daily_power'],
    'monthly',
    'DEVICE'
  );

  const dailyHistoryData = useMemo(() => {
    if (!historyData || !historyData['daily_power']) return [];
    
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const result = [];
    const points = historyData['daily_power'];

    for (let d = 1; d <= daysInMonth; d++) {
      const targetDate = new Date(today.getFullYear(), today.getMonth(), d);
      const startOfDay = new Date(targetDate.setHours(0, 0, 0, 0)).getTime();
      const endOfDay = new Date(targetDate.setHours(23, 59, 59, 999)).getTime();

      const point = points.find((p: any) => p.ts >= startOfDay && p.ts <= endOfDay);
      const kwh = point ? whToKwh(Number(point.value)) : 0;

      result.push({
        day: d.toString().padStart(2, '0'),
        kwh: d <= currentDay ? kwh : 0
      });
    }
    return result;
  }, [historyData, currentDay]);

  const todayDayStr = currentDay.toString().padStart(2, '0');

  // Fetch stats for all SEO / SEM / SER sensors
  useEffect(() => {
    let isMounted = true;
    async function fetchStats() {
      if (seoSensors.length === 0) return;
      try {
        setLoading(true);
        const targetKeys = [
          'daily_power', 'weekly_power', 'monthly_power',
          'daily_total_power', 'weekly_total_power', 'monthly_total_power',
          'power_daily', 'power_weekly', 'power_monthly',
          'today_energy', 'this_week_energy', 'this_month_energy',
          'power_instant', 'active_power', 'power'
        ];

        const promises = seoSensors.map(async (s) => {
          try {
            const data = await thingsboardService.getLatestTelemetry(
              'DEVICE',
              s.id,
              targetKeys
            ).catch(() => ({}));
            
            const extractEnergyKwh = (keys: string[]) => {
              // 1. Try fetched latest telemetry (Wh -> kWh, always divide by 1000)
              for (const k of keys) {
                const raw = data[k]?.[0]?.value;
                if (raw !== undefined && raw !== null && !isNaN(Number(raw))) {
                  const val = Number(raw);
                  if (val > 0) return val / 1000;
                }
              }
              // 2. Try real-time subscribed sensorTelemetry
              const sData = sensorTelemetry[s.id];
              if (sData) {
                for (const k of keys) {
                  const raw = sData[k]?.[0]?.value;
                  if (raw !== undefined && raw !== null && !isNaN(Number(raw))) {
                    const val = Number(raw);
                    if (val > 0) return val / 1000;
                  }
                }
                // 3. Fallback to instant power (W -> kWh)
                const inst = Number(sData.power_instant?.[0]?.value || sData.power?.[0]?.value || 0);
                if (inst > 0) {
                  return inst / 1000;
                }
              }
              return 0;
            };

            const daily = extractEnergyKwh(['daily_power', 'daily_total_power', 'power_daily', 'today_energy', 'power_instant']);
            const weekly = extractEnergyKwh(['weekly_power', 'weekly_total_power', 'power_weekly', 'this_week_energy']) || (daily * 1.5);
            const monthly = extractEnergyKwh(['monthly_power', 'monthly_total_power', 'power_monthly', 'this_month_energy']) || (daily * 3);

            return {
              id: s.id,
              daily,
              weekly,
              monthly
            };
          } catch (e) {
            return { id: s.id, daily: 0, weekly: 0, monthly: 0 };
          }
        });

        const results = await Promise.all(promises);
        const stats: Record<string, { daily: number; weekly: number; monthly: number }> = {};
        results.forEach(r => {
          stats[r.id] = { daily: r.daily, weekly: r.weekly, monthly: r.monthly };
        });
        
        if (isMounted) {
          setLatestStats(stats);
        }
      } catch (err) {
        console.error('Failed to fetch top outlet stats', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [seoSensors]);

  // 1. Separate sensors by Category (Multitab vs Outlet)
  const filteredSensors = useMemo(() => {
    if (deviceCategory === 'multitab') {
      // 스마트 멀티탭 (SEM)
      return seoSensors.filter(
        s => s.type === 'SEM' || s.name?.startsWith('SEM_') || s.label?.startsWith('SEM_')
      );
    } else {
      // 스마트 콘센트 (SEO, SER, PLUG, OUTLET)
      return seoSensors.filter(
        s => s.type === 'SEO' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET' ||
             s.name?.startsWith('SEO_') || s.name?.startsWith('SER_') ||
             s.label?.startsWith('SEO_') || s.label?.startsWith('SER_')
      );
    }
  }, [seoSensors, deviceCategory]);

  // 2. Sort TOP 3 within the selected category
  const sortedOutlets = useMemo(() => {
    const combined = filteredSensors.map(s => {
      const sStats = latestStats[s.id] || { daily: 0, weekly: 0, monthly: 0 };
      let usage = sStats.daily;
      if (timeRange === 'weekly') usage = sStats.weekly;
      else if (timeRange === 'monthly') usage = sStats.monthly;

      const rawName = s.name || s.id;
      const nickname = s.primary_appliance || (s.alias && s.alias !== rawName ? s.alias : '') || (s.label && s.label !== rawName ? s.label : '');
      const displayName = nickname || s.label || rawName;

      return {
        id: s.id,
        name: displayName,
        rawName: rawName,
        location: s.roomName || '',
        usage: usage,
        dailyUsage: sStats.daily,
        weeklyUsage: sStats.weekly,
        monthlyUsage: sStats.monthly,
        todayUsage: sStats.daily
      };
    }).sort((a, b) => b.usage - a.usage);

    const maxUsage = combined.length > 0 ? combined[0].usage : 1;
    return combined.map(o => ({
      ...o,
      percent: maxUsage > 0 ? Math.round((o.usage / maxUsage) * 100) : 0
    })).slice(0, 3); // Top 3 of selected category
  }, [filteredSensors, latestStats, timeRange]);

  return (
    <div className="w-full flex flex-col">
      {/* ─── 1. Section Header (카드 박스 바깥 상단) ─── */}
      <div className="flex items-center justify-between mb-2 px-1 text-[var(--mobile-text-muted)]">
        {/* 좌측: 타이틀 */}
        <div className="flex items-center gap-2">
          <PlugIcon className="w-4 h-4 text-violet-400" />
          <span className="text-[1rem] text-[var(--mobile-text-primary)] font-medium uppercase tracking-wider">
            사용량 많은 {deviceCategory === 'multitab' ? '멀티탭' : '콘센트'}
          </span>
        </div>

        {/* 우측 탭 그룹: [ 멀티탭 | 콘센트 ] + [ 일간 | 주간 | 월간 ] */}
        {!selectedOutlet && (
          <div className="flex items-center gap-1.5">
            {/* 1) 디바이스 분류 탭 [ 멀티탭 | 콘센트 ] */}
            <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/10 shadow-inner">
              <button
                onClick={() => setDeviceCategory('multitab')}
                className={`px-2 py-1 rounded-md text-[0.68rem] font-bold transition-all duration-200 ${
                  deviceCategory === 'multitab'
                    ? 'bg-violet-500 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                멀티탭
              </button>
              <button
                onClick={() => setDeviceCategory('outlet')}
                className={`px-2 py-1 rounded-md text-[0.68rem] font-bold transition-all duration-200 ${
                  deviceCategory === 'outlet'
                    ? 'bg-violet-500 text-white shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                콘센트
              </button>
            </div>

            {/* 2) 기간 선택 탭 [ 일간 | 주간 | 월간 ] */}
            <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/5">
              {(['daily', 'weekly', 'monthly'] as const).map((range) => (
                <button
                  key={range}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTimeRange(range);
                  }}
                  className={`
                    px-1.5 py-1 rounded-md text-[0.65rem] font-medium transition-all duration-200
                    ${timeRange === range 
                      ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' 
                      : 'text-slate-500 hover:text-slate-300'}
                  `}
                >
                  {range === 'daily' ? t('top_outlet.daily') : range === 'weekly' ? t('top_outlet.weekly') : t('top_outlet.monthly')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── 2. Content Card (어두운 카드 박스) ─── */}
      <div className="bg-[var(--mobile-bg-card)] border border-[var(--mobile-border)] shadow-sm rounded-2xl p-3.5 flex flex-col gap-3 overflow-hidden">
        <AnimatePresence mode="wait">
          {selectedOutlet ? (
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-3 flex-1 min-h-0"
            >
              {/* Detail Header */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedOutlet(null)}
                  className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                </button>
                <PlugIcon className="w-4 h-4 text-violet-400" />
                <span className="text-[1.0rem] font-medium text-[var(--mobile-text-primary)] tracking-wider truncate max-w-[120px]">
                  {selectedOutlet.name}
                </span>
                <span className="text-[0.7rem] text-[var(--mobile-text-muted)] font-mono">
                  {selectedOutlet.location}
                </span>
              </div>

              {/* Usage Summary Section */}
              <div className="flex items-center gap-3 bg-[var(--mobile-bg-sub)] rounded-xl px-3 py-2 border border-[var(--mobile-border-strong)]">
                <div className="p-1.5 rounded-lg bg-amber-500/15">
                  <BoltIcon className="w-3.5 h-3.5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="text-[0.65rem] text-[var(--mobile-text-muted)] uppercase tracking-wider">
                    {t('top_outlet.today_usage')}
                  </div>
                  <div className="text-lg font-mono font-bold text-[var(--mobile-text-primary)] leading-tight">
                    {selectedOutlet.dailyUsage.toFixed(3)}
                    <span className="text-[0.7rem] text-[var(--mobile-text-muted)] ml-1">kWh</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[0.65rem] text-[var(--mobile-text-muted)] uppercase tracking-wider">
                    {timeRange === 'daily' ? t('top_outlet.daily') : timeRange === 'weekly' ? t('top_outlet.weekly_cumulative') : t('top_outlet.monthly_cumulative')}
                  </div>
                  <div className="text-sm font-mono font-bold text-amber-400 leading-tight">
                    {(timeRange === 'daily' ? selectedOutlet.dailyUsage : timeRange === 'weekly' ? selectedOutlet.weeklyUsage : selectedOutlet.monthlyUsage).toFixed(3)}
                    <span className="text-[0.65rem] text-[var(--mobile-text-muted)] ml-1">
                      kWh
                    </span>
                  </div>
                </div>
              </div>

              {/* Daily Chart */}
              <div className="w-full flex flex-col relative mt-1">
                {historyLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[var(--mobile-bg-card)]/50 z-10">
                    <span className="text-[0.6rem] text-violet-400 animate-pulse">Loading Chart...</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 mb-1.5">
                  <CalendarIcon className="w-3 h-3 text-[var(--mobile-text-muted)]" />
                  <span className="text-[0.7rem] text-[var(--mobile-text-muted)] font-mono uppercase tracking-wider">
                    {monthLabel} {t('top_outlet.daily_usage_title')}
                  </span>
                </div>
                <div className="w-full h-[95px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyHistoryData} margin={{ top: 10, right: 0, left: -25, bottom: 0 }}>
                      <XAxis
                        dataKey="day"
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: 'var(--mobile-text-muted)', fontSize: 9, fontFamily: 'monospace' }}
                        interval={4}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: 'var(--mobile-text-muted)', fontSize: 8, fontFamily: 'monospace' }}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                        content={<CustomTooltip />}
                      />
                      <Bar dataKey="kwh" radius={[3, 3, 0, 0]}>
                        {dailyHistoryData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.day === todayDayStr ? '#a78bfa' : entry.day < todayDayStr ? 'rgba(167, 139, 250, 0.35)' : 'rgba(255, 255, 255, 0.1)'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col gap-2 flex-1 relative"
            >
              <div className="w-full">
                {sortedOutlets.length === 0 && !loading && (
                  <div className="text-center py-4 text-[0.75rem] text-[var(--mobile-text-muted)] italic">
                    해당 구역에 등록된 {deviceCategory === 'multitab' ? '멀티탭' : '콘센트'} 데이터가 없습니다.
                  </div>
                )}
                {loading && sortedOutlets.length === 0 && (
                  <div className="text-center py-4 text-[0.75rem] text-violet-400 animate-pulse">
                    {t('top_outlet.fetching_stats')}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  {sortedOutlets.map((outlet, i) => (
                    <motion.button
                      key={outlet.id}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => setSelectedOutlet(outlet)}
                      className="bg-[var(--mobile-bg-sub)] border border-[var(--mobile-border-strong)] rounded-xl p-2.5 flex flex-col justify-between text-left hover:bg-white/5 transition-colors min-h-[76px]"
                    >
                      {/* Header: Rank + Name */}
                      <div className="flex items-center gap-1.5 mb-1.5 min-w-0">
                        <div
                          className={`
                            w-4 h-4 rounded flex items-center justify-center text-[0.65rem] font-mono font-bold flex-shrink-0
                            ${i === 0 ? 'bg-[var(--mobile-rank-1-bg)] text-[var(--mobile-rank-1-text)]' : 'bg-[var(--mobile-rank-other-bg)] text-[var(--mobile-rank-other-text)]'}
                          `}
                        >
                          {i + 1}
                        </div>
                        <div className="text-xs font-semibold text-[var(--mobile-text-primary)] truncate flex-1">
                          {outlet.name}
                        </div>
                      </div>

                      {/* Power Usage Value */}
                      <div className="flex items-baseline justify-end gap-1 w-full">
                        <span
                          className={`
                            text-lg font-mono font-bold tracking-tight
                            ${i === 0 ? 'text-amber-400' : 'text-cyan-400'}
                          `}
                        >
                          {outlet.usage.toFixed(3)}
                        </span>
                        <span className="text-[0.65rem] text-[var(--mobile-text-muted)] font-mono">
                          kWh
                        </span>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
